import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import styles from "./ActionSheet.module.scss";
/**
 * Выезжающий снизу лист действий (bottom-sheet) для мобильных экранов —
 * замена выпадающему `Menu` там, где список действий удобнее показать крупными
 * тап-таргетами. Паттерн взаимодействия повторяет шторку деталей в GlobalPlayer:
 * оверлей + `motion.div` со свайпом вниз (drag за ручку), закрытие по оверлею/
 * ручке/свайпу, блокировка скролла фона. Рендерится порталом в `body`, чтобы не
 * обрезаться контейнерами со скроллом/overflow.
 */
export const ActionSheet = ({ open, onClose, items, title, }) => {
    // Тянем за ручку (dragListener=false), иначе перетаскивание перехватывало бы
    // тапы по пунктам.
    const dragControls = useDragControls();
    // Блокируем скролл фона, пока лист открыт.
    useEffect(() => {
        if (!open)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);
    const handleItemClick = (item) => {
        if (item.disabled)
            return;
        item.onClick?.();
        onClose();
    };
    return createPortal(_jsx(AnimatePresence, { children: open && (_jsxs(_Fragment, { children: [_jsx(motion.div, { className: styles.overlay, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 }, onClick: onClose }), _jsxs(motion.div, { className: styles.sheet, initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" }, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] }, drag: "y", dragListener: false, dragControls: dragControls, dragConstraints: { top: 0, bottom: 0 }, dragElastic: { top: 0, bottom: 0.4 }, onDragEnd: (_, info) => {
                        if (info.offset.y > 100 || info.velocity.y > 600) {
                            onClose();
                        }
                    }, role: "dialog", "aria-modal": "true", children: [_jsx("div", { className: styles.handle, onPointerDown: (e) => dragControls.start(e), "aria-hidden": "true" }), title != null && title !== "" && (_jsx("div", { className: styles.title, children: title })), _jsx("div", { className: styles.list, children: items.map((item, idx) => {
                                if (item === "divider") {
                                    return _jsx("div", { className: styles.divider }, `divider-${idx}`);
                                }
                                return (_jsxs("button", { type: "button", className: `${styles.item}${item.accent ? ` ${styles.itemAccent}` : ""}${item.danger ? ` ${styles.itemDanger}` : ""}${item.highlighted ? ` ${styles.itemHighlight}` : ""}`, disabled: item.disabled, onClick: () => handleItemClick(item), children: [item.icon && _jsx("span", { className: styles.itemIcon, children: item.icon }), _jsx("span", { className: styles.itemLabel, children: item.label })] }, item.id ?? `${idx}`));
                            }) })] })] })) }), document.body);
};
